import React, { useEffect, useState } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import CourseService from "../../services/Department/CourseService";
import styles from './Course.module.css';
import DepartmentBanner from "../common/DepartmentBanner";
import { FaSearch, FaSpinner, FaTable, FaTh, FaSort, FaSortUp, FaSortDown } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import Pagination from '../common/Pagination';
import DepartmentService from "../../services/Department/DepartmentService";

const Course = () => {
  document.title = "Courses";
  const params = new URLSearchParams(window.location.search);

  
  const [isLoading, setIsLoading] = useState(false);
  const [courses, setCourses] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const [currentPage, setCurrentPage] = useState(1);
  const [disabledColumns, setDisabledColumns] = useState([]);
  const itemsPerPage = 20;

  // Define column types
  const COLUMN_TYPES = Object.freeze({
    COURSE_INFORMATION: 6,
    COURSE_ACADEMIC_YEAR: 7,
    COURSE_SECTIONS: 8
  });

  useEffect(() => {
   
      // Get column configuration
      DepartmentService.GetColumnConfig(2)
        .then(response => {
          const { serviceDisabledItems } = response.data || response;
          if (serviceDisabledItems && serviceDisabledItems.trim() !== "") {
            setDisabledColumns(serviceDisabledItems.split(',').map(item => parseInt(item.trim())));
          } else {
            setDisabledColumns([]);
          }
        })
        .catch(error => {
          console.error('Error fetching column configuration:', error);
          setDisabledColumns([]);
        });

      fetchCourses();
    
  }, []);

  // Check if a column is enabled
  const isColumnEnabled = (columnType) => {
    return !disabledColumns.includes(columnType);
  };

  const fetchCourses = () => {
    setIsLoading(true);
    CourseService.GetByDepartmentId()
      .then(response => {
        setCourses(response.data.reverse());
        setIsLoading(false);
      })
      .catch(error => {
        setIsLoading(false);
        console.error('Error fetching page content:', error);
      });
  };

  const filteredCourses = courses.filter(course =>
    course.courseName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    course.courseNumber.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const sortedCourses = [...filteredCourses].sort((a, b) => {
    if (!sortConfig.key) return 0;
    
    const aValue = a[sortConfig.key];
    const bValue = b[sortConfig.key];
    
    if (aValue < bValue) return sortConfig.direction === 'ascending' ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === 'ascending' ? 1 : -1;
    return 0;
  });

  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = sortedCourses.slice(indexOfFirstItem, indexOfLastItem);

  const requestSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const getSortIcon = (columnName) => {
    if (sortConfig.key !== columnName) return <FaSort className="ms-2 text-muted" />;
    return sortConfig.direction === 'ascending' ? 
      <FaSortUp className="ms-2 text-primary" /> : 
      <FaSortDown className="ms-2 text-primary" />;
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo(0, 0);
  };
  
  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="container-fluid p-0" 
      style={{ backgroundColor: '#f8f9fa' }}
    >
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* <DepartmentBanner /> */}
        
        <div className="container-custom py-4">
          {/* Breadcrumb */}
          <nav aria-label="breadcrumb" className="mb-4">
            <ol className="breadcrumb bg-transparent p-0">
              <li className="breadcrumb-item">

              </li>
              <li className="breadcrumb-item active" aria-current="page">Course</li>
            </ol>
          </nav>

          {/* Stats Cards */}
          <div className="row mb-4">
            <div className="col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Total Courses</p>
                  <p className="mb-0">{courses.length}</p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Total Sections</p>
                  <p className="mb-0 h3">
                    {courses.reduce((sum, course) => sum + course.numberOfSection, 0)}
                  </p>
                </div>
              </div>
            </div>
            <div className="col-md-4">
              <div className="card border-0 shadow-sm">
                <div className="card-body">
                  <p className="text-muted mb-2 h3">Academic Years</p>
                  <p className="mb-0 h3">
                    {new Set(courses.map(course => course.academicYearName)).size}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Header Section */}
          <div className="card shadow-sm border-0 mb-4">
            <div className="card-body">
              <div className="row align-items-center">
                <div className="col-md-6">
                  <p className="mb-0 h3">Course Catalog</p>
                  <p className="text-muted mb-0">Browse available courses and sections</p>
                </div>
                <div className="col-md-6">
                  <div className="d-flex gap-3">
                    <div className="input-group">
                      <span className="input-group-text bg-white border-end-0">
                        <FaSearch className="text-muted" />
                      </span>
                      <input
                        type="text"
                        className="form-control border-start-0"
                        placeholder="Search courses..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                      />
                    </div>
                    <div className="btn-group">
                      <button type="button" aria-label="Open Table"
                        className={`btn ${viewMode === 'table' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('table')}
                      >
                        <FaTable />
                      </button>
                      <button type="button" aria-label="Open Grid"
                        className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
                        onClick={() => setViewMode('grid')}
                      >
                        <FaTh />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Content Section */}
          <AnimatePresence mode="wait">
            {isLoading ? (
              <div className="text-center py-5">
                <p className="text-muted">Loading courses...</p>
              </div>
            ) : viewMode === 'table' ? (
              <motion.div
                key="table"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="card shadow-sm border-0"
              >
                <div className="card-body p-0">
                  <div className="table-responsive">
                    <table className={`table ${styles.customTable}`}>
                      <thead>
                        <tr>
                          {isColumnEnabled(COLUMN_TYPES.COURSE_INFORMATION) && (
                            <th 
                              className="cursor-pointer" 
                              onClick={() => requestSort('courseName')}
                            >
                              Course Information
                              {getSortIcon('courseName')}
                            </th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.COURSE_ACADEMIC_YEAR) && (
                            <th 
                              className="cursor-pointer" 
                              onClick={() => requestSort('academicYearName')}
                            >
                              Academic Year
                              {getSortIcon('academicYearName')}
                            </th>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.COURSE_SECTIONS) && (
                            <th 
                              className="text-center cursor-pointer" 
                              onClick={() => requestSort('numberOfSection')}
                            >
                              Sections
                              {getSortIcon('numberOfSection')}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {currentItems.length > 0 ? (
                          currentItems.map((course, index) => (
                            <motion.tr 
                              key={index}
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: index * 0.05 }}
                            >
                              {isColumnEnabled(COLUMN_TYPES.COURSE_INFORMATION) && (
                                <td>
                                  <div>{course.courseName}</div>
                                  <div className={styles.courseNumber}>{course.courseNumber}</div>
                                </td>
                              )}
                              {isColumnEnabled(COLUMN_TYPES.COURSE_ACADEMIC_YEAR) && (
                                <td>
                                  <span className={styles.academicYear}>
                                    {course.academicYearName}
                                  </span>
                                </td>
                              )}
                              {isColumnEnabled(COLUMN_TYPES.COURSE_SECTIONS) && (
                                <td className="text-center">
                                  <span className={styles.sectionCount}>
                                    {course.numberOfSection} sections
                                  </span>
                                </td>
                              )}
                            </motion.tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={Object.values(COLUMN_TYPES).filter(type => isColumnEnabled(type)).length || 1} className="text-center py-5">
                              <p className="text-muted mb-0">No courses found</p>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="grid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                className="row g-4"
              >
                {currentItems.map((course, index) => (
                  <motion.div
                    key={index}
                    className="col-md-6 col-lg-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <div className="card h-100 border-0 shadow-sm hover-shadow">
                      <div className="card-body">
                        {isColumnEnabled(COLUMN_TYPES.COURSE_INFORMATION) && (
                          <>
                            <h5 className="card-title mb-3">{course.courseName}</h5>
                            <p className="card-text text-muted mb-3">{course.courseNumber}</p>
                          </>
                        )}
                        <div className="d-flex justify-content-between align-items-center">
                          {isColumnEnabled(COLUMN_TYPES.COURSE_ACADEMIC_YEAR) && (
                            <span className="badge bg-light text-dark">
                              {course.academicYearName}
                            </span>
                          )}
                          {isColumnEnabled(COLUMN_TYPES.COURSE_SECTIONS) && (
                            <span className="badge bg-primary rounded-pill">
                              {course.numberOfSection} sections
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {currentItems.length === 0 && (
                  <div className="col-12 text-center py-5">
                    <p className="text-muted mb-0">No courses found</p>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* Pagination */}
          <Pagination
            currentPage={currentPage}
            totalItems={sortedCourses.length}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
          />
        </div>
      </div>
    </motion.div>
  );
};

export default Course;
